import { z } from "zod";
import { baseEntitySchema } from "./base";

// An outgoing Airwallex Bank payment pulled in by the read-only sync, held in an
// inbox to be reconciled against a purchase order. "suggested" = the sync found
// a likely PO by counterparty + amount; "matched" = an admin confirmed (or
// manually picked) the PO; "rejected" = an admin dismissed it; "unmatched" = no
// suggestion.
export const airwallexPaymentMatchStatusSchema = z.enum([
  "unmatched",
  "suggested",
  "matched",
  "rejected",
]);
export type AirwallexPaymentMatchStatus = z.infer<
  typeof airwallexPaymentMatchStatusSchema
>;

export const airwallexPaymentDataSchema = z.object({
  transactionId: z.string(),
  accountId: z.string(),
  accountName: z.string().optional(),
  amount: z.number().nonnegative(),
  currency: z.string().default("USD"),
  feeValue: z.number().nonnegative().default(0),
  // "outgoing" = money we sent; "incoming" = money received. The sync currently
  // ingests only outgoing; stored for forward-compatible filtering.
  direction: z.enum(["outgoing", "incoming"]).optional(),
  counterpartyId: z.string().optional(),
  counterpartyName: z.string().optional(),
  paidAt: z.date().optional(),
  kind: z.string().optional(),
  airwallexStatus: z.string().optional(),
  bankDescription: z.string().optional(),
  matchStatus: airwallexPaymentMatchStatusSchema,
  // Outgoing → purchase order; incoming → sales order's invoice (direction picks).
  suggestedPoId: z.string().optional(),
  matchedPoId: z.string().optional(),
  suggestedInvoiceId: z.string().optional(),
  matchedInvoiceId: z.string().optional(),
  suggestedSoId: z.string().optional(),
  matchedSoId: z.string().optional(),
  suggestionMismatchPct: z.number().optional(),
  matchedPaymentId: z.string().optional(),
  matchedAt: z.date().optional(),
  matchedByUserId: z.string().optional(),
});

export type AirwallexPaymentData = z.infer<typeof airwallexPaymentDataSchema>;
export const airwallexPaymentSchema = baseEntitySchema.merge(
  airwallexPaymentDataSchema,
);
export type AirwallexPayment = z.infer<typeof airwallexPaymentSchema>;
